import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { VpnKey } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../services/api';
import GenerateEmergencyCodeModal, { EmergencyCodeTarget } from './GenerateEmergencyCodeModal';
import moment from 'moment-timezone';

const EmergencyQuickActions: React.FC = () => {
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const canGenerate = permissions.includes('monitoring.emergency_access.generate');
  const canEnd = permissions.includes('monitoring.emergency_access.end');

  const [pickerOpen, setPickerOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [target, setTarget] = useState<EmergencyCodeTarget | null>(null);

  const { data: devicesData } = useQuery({
    queryKey: ['monitoringDevicesPicker'],
    queryFn: () => apiClient.monitoring.getDevices(),
    enabled: pickerOpen && canGenerate,
  });

  const { data: activeData, refetch: refetchActive } = useQuery({
    queryKey: ['activeEmergencyBypass'],
    queryFn: () => apiClient.monitoring.getActiveEmergencyAccess(),
    enabled: permissions.includes('monitoring.emergency_access.read') || canGenerate,
    refetchInterval: 30000,
  });

  const options = useMemo(() => {
    const devices = devicesData?.data?.data || [];
    return devices.map((device: any) => ({
      key: device.id,
      label: `${device.employee?.name || 'Unassigned'} — ${device.device_name || device.device_id || device.id}`,
      employeeId: device.employee?.id ?? null,
      employeeName: device.employee?.name ?? null,
      deviceId: device.id,
      deviceName: device.device_name || device.device_id || device.id,
      campaignName: device.campaign_name ?? null,
    }));
  }, [devicesData]);

  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options.slice(0, 25);
    return options.filter((option) => option.label.toLowerCase().includes(term)).slice(0, 25);
  }, [options, search]);

  const activeRows = activeData?.data?.data || [];

  const openGenerateFor = (nextTarget: EmergencyCodeTarget) => {
    setTarget(nextTarget);
    setPickerOpen(false);
    setModalOpen(true);
  };

  const handleEndBypass = async (codeId: string) => {
    await apiClient.monitoring.endEmergencyBypass(codeId);
    refetchActive();
  };

  if (!canGenerate && activeRows.length === 0) {
    return null;
  }

  return (
    <>
      {canGenerate && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Quick Actions</Typography>
            <Button variant="contained" startIcon={<VpnKey />} onClick={() => setPickerOpen(true)}>
              Generate Emergency Code
            </Button>
          </CardContent>
        </Card>
      )}

      {activeRows.length > 0 && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Active Emergency Access</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Computer</TableCell>
                  <TableCell>Authorized By</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeRows.map((row: any) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.employee ? `${row.employee.firstName} ${row.employee.lastName}` : '—'}</TableCell>
                    <TableCell>{row.device?.computerName || row.device?.deviceName || row.device?.deviceId || '—'}</TableCell>
                    <TableCell>{row.authorizedByUser ? `${row.authorizedByUser.firstName} ${row.authorizedByUser.lastName}` : '—'}</TableCell>
                    <TableCell>{row.reason || row.reasonCode || '—'}</TableCell>
                    <TableCell>{row.bypassExpiresAt ? moment(row.bypassExpiresAt).format('h:mm A') : '—'}</TableCell>
                    <TableCell align="right">
                      {canEnd && (
                        <Button size="small" onClick={() => handleEndBypass(row.id)}>End Bypass</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Select Employee / Computer</DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <Autocomplete
              options={filteredOptions}
              getOptionLabel={(option) => option.label}
              onInputChange={(_, value) => setSearch(value)}
              onChange={(_, value) => {
                if (value) {
                  openGenerateFor({
                    employeeId: value.employeeId,
                    employeeName: value.employeeName,
                    deviceId: value.deviceId,
                    deviceName: value.deviceName,
                    campaignName: value.campaignName,
                  });
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Search employee or computer" placeholder="Type to search..." />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <GenerateEmergencyCodeModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          refetchActive();
        }}
        target={target}
      />
    </>
  );
};

export default EmergencyQuickActions;
